import { z } from 'zod';
import { JobType, RunStatus } from '../enums';
import { RecommendationImportFormat } from '../import-formats';

export const DriftDetails = z.object({
  contractVersion: z.string(),
  unknownHeaders: z.array(z.string()),
  missingColumns: z.array(z.string()),
  missingIdColumns: z.array(z.string()),
  duplicateHeaders: z.array(z.string()),
});
export type DriftDetails = z.infer<typeof DriftDetails>;

export const IngestionRunDto = z.object({
  id: z.string().uuid(),
  jobType: JobType,
  fileName: z.string().nullable(),
  fileHash: z.string().nullable(),
  status: RunStatus,
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  rowsRead: z.number().int(),
  rowsUpserted: z.number().int(),
  rowsRejected: z.number().int(),
  schemaDriftDetected: z.boolean(),
  driftDetails: DriftDetails.nullable(),
  errorSummary: z.string().nullable(),
  triggeredByName: z.string().nullable(),
});
export type IngestionRunDto = z.infer<typeof IngestionRunDto>;

export const IngestionRowErrorDto = z.object({
  rowNumber: z.number().int(),
  identifier: z.string().nullable(),
  error: z.string(),
});
export type IngestionRowErrorDto = z.infer<typeof IngestionRowErrorDto>;

export const IngestionRunDetailDto = IngestionRunDto.extend({
  rowErrors: z.array(IngestionRowErrorDto),
});
export type IngestionRunDetailDto = z.infer<typeof IngestionRunDetailDto>;

export const RecommendationImportQuery = z.object({
  format: RecommendationImportFormat,
  cycleId: z.string().uuid().optional(),
  headerless: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export type RecommendationImportQuery = z.infer<typeof RecommendationImportQuery>;

export const RecommendationImportResult = z.object({
  runId: z.string().uuid(),
  rowsRead: z.number().int(),
  imported: z.number().int(),
  skippedDuplicates: z.number().int(),
  householdsCreated: z.number().int(),
  possibleDuplicates: z.number().int(),
  rejected: z.array(z.object({ rowNumber: z.number().int(), error: z.string() })),
  ignoredHeaders: z.array(z.string()),
});
export type RecommendationImportResult = z.infer<typeof RecommendationImportResult>;

export const AuditLogDto = z.object({
  id: z.number().int(),
  at: z.string(),
  actorType: z.enum(['user', 'system', 'job']),
  actorLabel: z.string(),
  action: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type AuditLogDto = z.infer<typeof AuditLogDto>;

export const AuditLogQuery = z.object({
  action: z.string().max(100).optional(),
  entityId: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  before: z.coerce.number().int().optional(),
});
export type AuditLogQuery = z.infer<typeof AuditLogQuery>;
